import { AgGridColumn } from 'ag-grid-react';
import { pipe } from 'ramda';
import React, { FC, useCallback, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { PartnerCompany, PartnerPermission } from 'api/partner/types';
import { withExternalFilter } from 'components/atoms/grid';
import { ReactivationBillingWrapper } from 'components/organisms/module-settings/team-members/list/more-actions-menu/billing-wrapper';
import { RoutePaths } from 'constant';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import { useReactivatePartnerMemberEstimateMutation } from 'queries/billing';
import { sortByText } from 'utils/sort-by-text';
import { ReactivateStatus, usePartnerStatusActions } from './actions';
import { Grid, GridContainer } from './component';
import { PartnerListNoSearchResultState } from './empty-state';
import {
  PartnerNameRenderer,
  TeamMembersRenderer,
  AccessLevelRenderer,
  PartnerActiveStatusRenderer,
  PartnerAdminRenderer,
  AccessLevelSorter,
  StatusSorter,
} from './table-cells';

interface Props {
  partners: PartnerCompany[];
  partnerPermissions: PartnerPermission[];
  filterFunction: (record) => boolean;
}
export const AgGrid = pipe(pipe(() => Grid, withExternalFilter))();
const onModelUpdated = ({ api }) => {
  if (api.getDisplayedRowCount() === 0) {
    api.showNoRowsOverlay();
  } else {
    api.hideOverlay();
  }
};

export const PartnerListGrid: FC<Props> = ({
  partners,
  partnerPermissions,
  filterFunction,
}) => {
  const translate = useTranslate();
  const { path } = useRouteMatch();
  const { push } = useHistory();
  const isBillingEnabled = useBillingFeatureFlag();
  const viewPartner = useCallback(
    (partner: PartnerCompany) => {
      push(
        `${path}${RoutePaths.partnerInformation.replace(
          ':id',
          partner.companyId.toString(),
        )}`,
      );
    },
    [path, push],
  );
  const {
    changeActiveStatus,
    partnerForReactivatingBillingFlow,
    adminForReactivatePartnerCompany,
    reactivateStatus,
    onBillingFlowConfirm,
    onBillingFlowClose,
  } = usePartnerStatusActions();
  const sortedPartners = useMemo(
    () =>
      [...partners]
        .sort((partnerA, partnerB) => partnerB.createdAt - partnerA.createdAt)
        .sort((partnerA, partnerB) =>
          StatusSorter(partnerA.active, partnerB.active),
        ),
    [partners],
  );

  return (
    <>
      <GridContainer className="ag-theme-balham">
        <AgGrid
          suppressContextMenu
          suppressRowClickSelection
          suppressCellSelection
          defaultColDef={{
            filter: false,
            resizable: false,
            suppressMovable: true,
            suppressMenu: true,
            unSortIcon: true,
            wrapText: true,
          }}
          getRowNodeId={(data) => data.companyId}
          size="large"
          rowData={sortedPartners}
          noRowsOverlayComponent="PartnerListNoSearchResultState"
          frameworkComponents={{
            PartnerNameRenderer,
            TeamMembersRenderer,
            AccessLevelRenderer,
            PartnerActiveStatusRenderer,
            PartnerAdminRenderer,
            PartnerListNoSearchResultState,
          }}
          filter={filterFunction}
          onModelUpdated={onModelUpdated}
        >
          <AgGridColumn
            flex={1}
            field="companyId"
            editable={false}
            headerName={translate('name')}
            cellRenderer="PartnerNameRenderer"
            cellClass="clickable-cell"
            onCellClicked={({ data }) => viewPartner(data)}
            sortable
            comparator={(
              valueA,
              valueB,
              { data: recordA },
              { data: recordB },
            ) => sortByText(recordA.companyName, recordB.companyName)}
          />
          <AgGridColumn
            flex={1}
            field="admins"
            editable={false}
            cellClass="clickable-cell"
            onCellClicked={({ data }) => viewPartner(data)}
            cellRenderer="PartnerAdminRenderer"
            headerName={translate(
              'partners.new.drawer.form.partner_admin.label',
            )}
          />
          <AgGridColumn
            width={200}
            field="usedSeat"
            editable={false}
            cellClass="clickable-cell"
            onCellClicked={({ data }) => viewPartner(data)}
            headerName={translate('team_members')}
            cellRenderer="TeamMembersRenderer"
            sortable
            comparator={(valueA, valueB) => valueA - valueB}
          />
          <AgGridColumn
            width={152}
            field="accessLevel"
            editable={false}
            cellClass="clickable-cell"
            onCellClicked={({ data }) => viewPartner(data)}
            headerName={translate('partners.list.columns.access_level.label')}
            cellRenderer="AccessLevelRenderer"
            cellRendererParams={{
              partnerPermissions,
            }}
            sortable
            comparator={(valueA, valueB) =>
              AccessLevelSorter(
                valueA.partnerPermissionSetName,
                valueB.partnerPermissionSetName,
              )
            }
          />
          <AgGridColumn
            width={152}
            field="active"
            editable={false}
            headerName={translate('status')}
            cellRenderer="PartnerActiveStatusRenderer"
            cellRendererParams={{ changeActiveStatus }}
            sortable
            comparator={StatusSorter}
          />
        </AgGrid>
      </GridContainer>
      {isBillingEnabled &&
        partnerForReactivatingBillingFlow &&
        adminForReactivatePartnerCompany && (
          <ReactivationBillingWrapper
            visible={partnerForReactivatingBillingFlow != null}
            user={adminForReactivatePartnerCompany}
            onSubmit={onBillingFlowConfirm}
            isProcessing={
              reactivateStatus[partnerForReactivatingBillingFlow.companyId] ===
              ReactivateStatus.Reactivating
            }
            isSuccess={
              reactivateStatus[partnerForReactivatingBillingFlow.companyId] ===
              ReactivateStatus.Reactivated
            }
            isError={
              reactivateStatus[partnerForReactivatingBillingFlow.companyId] ===
              ReactivateStatus.Failed
            }
            onCancel={onBillingFlowClose}
            estimateMutation={useReactivatePartnerMemberEstimateMutation}
          />
        )}
    </>
  );
};
